import { Controller } from "@hotwired/stimulus";

// Universal client-side conversion event tracker.
// Fires fbq() and gtag() events with a shared UUID for CAPI dedup.
//
// Usage modes (set via data-conversion-tracker-mode-value):
//   "submit" -- fires on form submit, injects hidden field with event ID (default)
//   "connect" -- fires on connect (page load), with sessionStorage dedup
/* global fbq, gtag */
export default class extends Controller {
  static targets = ["form"];

  static values = {
    eventName: String,
    mode: { type: String, default: "submit" },
    eventId: String,
    contentName: String,
    price: Number,
    currency: { type: String, default: "USD" },
  };

  static GTAG_EVENTS = {
    CompleteRegistration: "sign_up",
    StartTrial: "start_trial",
    InitiateCheckout: "begin_checkout",
    Purchase: "purchase",
  };

  connect() {
    this._eventId = this._resolveEventId();

    if (this.modeValue === "submit") {
      this._injectHiddenField();
    }

    if (this.modeValue === "connect") {
      this._fireOnce();
    }
  }

  fire() {
    this._firePixels(this._eventId);
  }

  // Private

  _resolveEventId() {
    if (this.hasEventIdValue && this.eventIdValue) return this.eventIdValue;

    const urlParam = new URLSearchParams(window.location.search).get("cei");
    if (urlParam) return urlParam;

    if (this.modeValue === "connect") return null;

    return crypto.randomUUID();
  }

  _fireOnce() {
    if (!this._eventId) return;

    const key = `conversion_${this.eventNameValue}_fired`;
    if (sessionStorage.getItem(key)) return;

    this._firePixels(this._eventId);
    sessionStorage.setItem(key, "1");
  }

  _firePixels(eventId) {
    const customData = {};
    if (this.hasContentNameValue)
      customData.content_name = this.contentNameValue;
    if (this.hasPriceValue) {
      customData.value = this.priceValue;
      customData.currency = this.currencyValue;
    }

    if (typeof fbq === "function") {
      fbq("track", this.eventNameValue, customData, { eventID: eventId });
    }

    const gtagEvent = this.constructor.GTAG_EVENTS[this.eventNameValue];
    if (typeof gtag === "function" && gtagEvent) {
      const gtagData = { transaction_id: eventId };
      if (this.hasContentNameValue) {
        gtagData.items = [{ item_name: this.contentNameValue }];
      }
      if (this.hasPriceValue) {
        gtagData.value = this.priceValue;
        gtagData.currency = this.currencyValue;
      }
      gtag("event", gtagEvent, gtagData);
    }
  }

  _injectHiddenField() {
    const form = this.hasFormTarget
      ? this.formTarget
      : this.element.querySelector("form");
    if (!form) return;

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "cei";
    hidden.value = this._eventId;
    form.appendChild(hidden);
  }
}
